// The text under a video (Description.vue) is Vimeo's, plain: the people sheet's Bio and
// Description with their HTML (links, bold) stripped (fullstack scripts/update/videos/
// descriptions.js). This finds the sheet's own HTML for a paragraph, so the video page keeps the
// links the People highlights show (2026-10-14). A video uploaded to Vimeo by hand (e.g. Shorts
// 2025) has no bio in its text: getSpeakerBio finds one in the People sheets.
import history from "@/assets/history.json";
import {getSpeakerKey} from "@/composables/useSpeakerIndex";
import {fetchProgramSheet} from "@/composables/useProgramSheet";

const HTML_COLUMNS = ["Bio", "Description"];

const YEARS = Object.keys(history.years).map(Number).filter(Boolean);

async function fetchYearRows(year) {
  return (await fetchProgramSheet(year)).speakers || [];
}

// the text as Vimeo has it: no tags or entities, whitespace collapsed
// a cell's HTML on one line: the sheet's cells are often hard-wrapped ("Marta\nSzachniuk"), and
// the video page keeps line breaks (white-space: pre-wrap)
const getOneLine = (html) => String(html || "").replace(/\s+/g, " ").trim();

export function getPlainText(html) {
  const text = new DOMParser().parseFromString(String(html || ""), "text/html").body.textContent;
  return text.replace(/\s+/g, " ").trim();
}

// {plain text: HTML} for the Bio and Description cells of these speakers' rows in that year
export async function getSheetHtmlByText(year, speakerKeys) {
  const [headers = [], ...rows] = await fetchYearRows(year);
  const nameColumn = headers.indexOf("Name");
  const columns = HTML_COLUMNS.map((heading) => headers.indexOf(heading)).filter((i) => i >= 0);
  const byText = {};
  for (const row of rows) {
    if (!speakerKeys.includes(getSpeakerKey(row[nameColumn] || ""))) continue;
    for (const column of columns) {
      const html = getOneLine(row[column]);
      if (/<[a-z]/i.test(html)) byText[getPlainText(html)] = html;
    }
  }
  return byText;
}

async function getBioInYear(year, speakerKey) {
  const [headers = [], ...rows] = await fetchYearRows(year);
  const [nameColumn, bioColumn] = [headers.indexOf("Name"), headers.indexOf("Bio")];
  if (bioColumn < 0) return "";
  const row = rows.find((cells) => getSpeakerKey(cells[nameColumn] || "") === speakerKey);
  return getOneLine(row?.[bioColumn]);
}

// a speaker's bio (the sheet's HTML) for a video with none in its text: the video's year, else the
// nearest earlier year, else the latest; "" if no sheet has one
export async function getSpeakerBio(year, speakerKey) {
  const years = [...new Set(YEARS)].sort((a, b) => b - a);
  const earlier = years.filter((each) => each <= Number(year));
  const later = years.filter((each) => each > Number(year));
  for (const each of [...earlier, ...later]) {
    const bio = await getBioInYear(each, speakerKey);
    if (bio) return bio;
  }
  return "";
}
